import _ from 'lodash';
import { deliveryObject } from '@/utils/deliveryObject';
import { isConfectionAmountEnterable } from '@/utils/confection/nde';
import { DeliveryAddress, DeliveryPointData } from '@/types/delivery-point';
import { ConfectionProduct, DeliveryPointProduct, Product } from '@/types/product';
import { ConfectionDataWithDeliveryId, ConfectionState } from '@/types/confection';
import { CartItem } from '@/types/cart';
import { normalizeNumber } from '../normalize';

const createDeliveryAddressFromFile = (deliveryPoint: DeliveryPointData): DeliveryAddress => ({
  ...deliveryObject,
  token: deliveryPoint.token || '',
  delivery_file: deliveryPoint.filename || '',
  companyName: deliveryPoint.companyName || '',
  street: deliveryPoint.street || '',
  city: deliveryPoint.city || '',
  zipCode: deliveryPoint.zipCode || '',
  companyPhone: deliveryPoint.personOnePhone || '',
  personOneName: deliveryPoint.personOneName || '',
  personOneSurname: deliveryPoint.personOneSurname || '',
  personOnePhone: deliveryPoint.personOnePhone || '',
  personSecondName: deliveryPoint.personSecondName || '',
  personSecondSurname: deliveryPoint.personSecondSurname || '',
  personSecondPhone: deliveryPoint.personSecondPhone || '',
});

const createCartItem = (
  product: Product | ConfectionProduct,
  productId: number | string,
  deliveryId: number,
  quantity: number | string,
  price: number | string | null,
): CartItem => ({
  id: productId,
  iddelivery: deliveryId,
  name: product.name,
  price: normalizeNumber(price),
  quantity: normalizeNumber(quantity),
  product_category: product.product_category,
  analyticsData: product.analyticsData,
});

const getSelectedConfectionProducts = (
  mainProduct: Product,
  confectionState: ConfectionState,
): ConfectionProduct[] => {
  const selectedItems = confectionState.selectedConfectionItems || {};
  const { selectedExclusiveItem } = confectionState;
  const allConfectionItems = [
    ...(Array.isArray(mainProduct.confection) ? mainProduct.confection : []),
    ...(Array.isArray(mainProduct.confectionService) ? mainProduct.confectionService : []),
  ];

  return _.uniqBy(
    allConfectionItems.filter(
      item => item && (selectedExclusiveItem === item.slug || Boolean(selectedItems[item.slug])),
    ),
    'slug',
  );
};

type ConfectionQuantities = Record<string | number, number>;

const getEnterableConfectionQuantities = (
  cartItems: CartItem[],
): ConfectionQuantities => {
  const quantitiesById: ConfectionQuantities = {};

  cartItems
    .filter(item => Number(item.iddelivery) === 0)
    .filter(isConfectionAmountEnterable)
    .forEach((item) => {
      quantitiesById[item.id] = normalizeNumber(item.quantity);
    });

  return quantitiesById;
};

const calculateConfectionQuantity = (
  deliveryId: number,
  product: ConfectionProduct,
  initialQuantity: unknown,
  totalProductQuantity: number,
) => {
  if (!isConfectionAmountEnterable(product)) {
    return totalProductQuantity;
  }

  if (deliveryId !== 0) return 0;

  return normalizeNumber(initialQuantity);
};

type BuildStateArgs = {
  deliveryPoints: DeliveryPointData[];
  mainProduct: Product;
  deliveryPointProduct: DeliveryPointProduct | null;
  confectionState: ConfectionState;
  previousCart: CartItem[];
  createAdditionalProductId: (deliveryId: number) => number | string;
};

type BuildStateResult = {
  addresses: DeliveryAddress[];
  cartItems: CartItem[];
  confectionData: ConfectionDataWithDeliveryId[];
};

export const buildNdeDeliveryPointsStateFromFile = ({
  deliveryPoints,
  mainProduct,
  deliveryPointProduct,
  confectionState,
  previousCart,
  createAdditionalProductId,
}: BuildStateArgs): BuildStateResult => {
  const selectedConfectionProducts = getSelectedConfectionProducts(mainProduct, confectionState);
  const confectionQuantities = getEnterableConfectionQuantities(previousCart);
  const cartItems: CartItem[] = [];
  const confectionData: ConfectionDataWithDeliveryId[] = [];

  if (deliveryPointProduct) {
    cartItems.push(
      createCartItem(
        deliveryPointProduct,
        deliveryPointProduct.idproducts,
        0,
        deliveryPoints.length || 1,
        deliveryPointProduct.price,
      ),
    );
  }

  deliveryPoints.forEach((deliveryPoint, deliveryId) => {
    const products = Array.isArray(deliveryPoint.products) ? deliveryPoint.products : [];
    let totalMainProductQuantity = 0;

    products.forEach((productData, productIndex) => {
      const quantity = normalizeNumber(productData.numberOfProducts);
      const price = normalizeNumber(productData.value);
      totalMainProductQuantity += quantity;

      const productId = productIndex > 0 ? createAdditionalProductId(deliveryId) : mainProduct.idproducts;

      cartItems.push(
        createCartItem(mainProduct, productId, deliveryId, quantity, price),
      );
    });

    selectedConfectionProducts.forEach((confectionProduct) => {
      const initialQuantity = confectionQuantities[confectionProduct.idproducts];
      const confectionQuantity = calculateConfectionQuantity(
        deliveryId,
        confectionProduct,
        initialQuantity,
        totalMainProductQuantity,
      );

      cartItems.push(
        createCartItem(
          confectionProduct,
          confectionProduct.idproducts,
          deliveryId,
          confectionQuantity,
          confectionProduct.price,
        ),
      );
    });

    if (Array.isArray(deliveryPoint.confection)) {
      confectionData.push(
        ...deliveryPoint.confection.map(item => ({
          ...item,
          deliveryId,
        })),
      );
    }
  });

  return {
    addresses: deliveryPoints.map(createDeliveryAddressFromFile),
    cartItems,
    confectionData,
  };
};
